import React from 'react'
import { Button, Divider, Select, Space, Tooltip, Typography, Image } from 'antd'
import { t } from '@/i18n'
import { IconFont } from '@/components'
import { FilterDropdownProps } from 'antd/lib/table/interface'
import { formatDateTime } from '@/utils/date'
import { riskLevelMap, interceptStatusMap, resourceLevelMap, logAlarmMap } from './constants'

const { Text } = Typography

const FilterIcon = ({ filtered }: { filtered?: boolean }) => (
  <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
    <IconFont
      type="icon-a-shaixuan1"
      style={{
        fontSize: '14px',
        color: filtered ? '#4238C9' : '#999',
        cursor: 'pointer',
        transition: 'color 0.3s',
      }}
    />
  </Tooltip>
)

export const createTableColumns = (
  taskInfo: any,
  attackType: any[],
  getDrawerData: (id: string) => void,
  setActive: (id: string) => void,
  riskLevelSelectOpen: boolean,
  setRiskLevelSelectOpen: (open: boolean) => void,
  attackTypeSelectOpen: boolean,
  setAttackTypeSelectOpen: (open: boolean) => void,
  resourceLevelSelectOpen: boolean,
  setResourceLevelSelectOpen: (open: boolean) => void,
  interceptStatusSelectOpen: boolean,
  setInterceptStatusSelectOpen: (open: boolean) => void,
  deviceStatusSelectOpen: boolean,
  setDeviceStatusSelectOpen: (open: boolean) => void,
  filterParams: any,
) => {
  const baseColumns = [
    {
      title: t('用例名称'),
      dataIndex: 'pluginName',
      key: 'pluginName',
      width: '20%',
      render: (_: any, record: any) => (
        <Text
          ellipsis={{
            tooltip: {
              getPopupContainer: () => document.body,
            },
          }}
          className="case_name"
          style={{ width: 250, cursor: 'pointer' }}
          onClick={() => {
            getDrawerData(record.id)
          }}
        >
          {record?.pluginName}
        </Text>
      ),
    },
    {
      title: t('危险等级'),
      dataIndex: 'riskLevel',
      key: 'riskLevel',
      width: '10%',
      filteredValue: filterParams.riskLevel,
      render: (_: any, record: any) => (
        <div>
          <Image src={riskLevelMap[record?.riskLevel]?.iconSrc} width={19} />
          <span className={riskLevelMap[record?.riskLevel]?.class}>
            {riskLevelMap[record?.riskLevel]?.label}
          </span>
        </div>
      ),
      filterDropdown: (props: FilterDropdownProps) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            mode="multiple"
            allowClear
            maxTagCount={'responsive'}
            style={{ width: '100%' }}
            placeholder={t('请选择危险等级')}
            value={props.selectedKeys as string[]}
            open={riskLevelSelectOpen}
            getPopupContainer={() => document.body as HTMLElement}
            popupClassName="table-filter-dropdown"
            onDropdownVisibleChange={setRiskLevelSelectOpen}
            onChange={value => {
              props.setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.clearFilters?.()
                    }}
                  >
                    {t('重置')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.confirm()
                      setRiskLevelSelectOpen(false)
                    }}
                  >
                    {t('确定')}
                  </Button>
                </Space>
              </div>
            )}
            options={[
              { label: t('严重'), value: 'risk_level_critical' },
              { label: t('高危'), value: 'risk_level_high' },
              { label: t('中危'), value: 'risk_level_medium' },
              { label: t('低危'), value: 'risk_level_low' },
            ]}
          />
        </div>
      ),
      filterIcon: (filtered: boolean) => <FilterIcon filtered={filtered} />,
    },
    {
      title: t('攻击类型'),
      dataIndex: 'attackType',
      key: 'attackType',
      width: '10%',
      filteredValue: filterParams.attackType,
      render: (_: any, record: any) => (
        <Text
          ellipsis={{ tooltip: { getPopupContainer: () => document.body } }}
          style={{ width: 100 }}
        >
          {record?.attackType}
        </Text>
      ),
      filterDropdown: (props: FilterDropdownProps) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            allowClear
            mode="multiple"
            maxTagCount={'responsive'}
            style={{ width: '100%' }}
            placeholder={t('请选择攻击类型')}
            value={props.selectedKeys as string[]}
            open={attackTypeSelectOpen}
            getPopupContainer={() => document.body as HTMLElement}
            popupClassName="table-filter-dropdown"
            onDropdownVisibleChange={setAttackTypeSelectOpen}
            onChange={value => {
              props.setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.clearFilters?.()
                    }}
                  >
                    重置
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.confirm()
                      setAttackTypeSelectOpen(false)
                    }}
                  >
                    确定
                  </Button>
                </Space>
              </div>
            )}
            options={attackType.map(item => ({ label: item, value: item }))}
          />
        </div>
      ),
      filterIcon: (filtered: boolean) => <FilterIcon filtered={filtered} />,
    },
    {
      title: t('用例分级'),
      dataIndex: 'resourceLevel',
      key: 'resourceLevel',
      width: '8%',
      filteredValue: filterParams.resourceLevel,
      filterDropdown: (props: FilterDropdownProps) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            mode="multiple"
            allowClear
            maxTagCount={'responsive'}
            style={{ width: '100%' }}
            placeholder={t('请选择用例分级')}
            value={props.selectedKeys as string[]}
            open={resourceLevelSelectOpen}
            getPopupContainer={() => document.body as HTMLElement}
            popupClassName="table-filter-dropdown"
            onDropdownVisibleChange={setResourceLevelSelectOpen}
            onChange={value => {
              props.setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.clearFilters?.()
                    }}
                  >
                    重置
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.confirm()
                      setResourceLevelSelectOpen(false)
                    }}
                  >
                    确定
                  </Button>
                </Space>
              </div>
            )}
            options={[
              { label: t('初级'), value: 'ResourceLevelJunior' },
              { label: t('中级'), value: 'ResourceLevelMiddle' },
              { label: t('高级'), value: 'ResourceLevelSenior' },
            ]}
          />
        </div>
      ),
      filterIcon: (filtered: boolean) => <FilterIcon filtered={filtered} />,
      render: (text: number) => (
        <div>
          <span>{resourceLevelMap[text]?.label || '-'}</span>
        </div>
      ),
    },
    {
      title: t('验证结果'),
      dataIndex: 'status',
      key: 'status',
      width: '10%',
      filteredValue: filterParams.status,
      filterDropdown: (props: FilterDropdownProps) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            mode="multiple"
            allowClear
            maxTagCount={'responsive'}
            style={{ width: '100%' }}
            placeholder={t('请选择验证结果')}
            value={props.selectedKeys as string[]}
            open={interceptStatusSelectOpen}
            getPopupContainer={() => document.body as HTMLElement}
            popupClassName="table-filter-dropdown"
            onDropdownVisibleChange={setInterceptStatusSelectOpen}
            onChange={value => {
              props.setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.clearFilters?.()
                    }}
                  >
                    重置
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.confirm()
                      setInterceptStatusSelectOpen(false)
                    }}
                  >
                    确定
                  </Button>
                </Space>
              </div>
            )}
            options={[
              { label: t('拦截成功'), value: 'InterceptStatusSuccess' },
              { label: t('防护失效'), value: 'InterceptStatusDefenceFail' },
              { label: t('检测成功'), value: 'InterceptStatusDetectSuccess' },
            ]}
          />
        </div>
      ),
      filterIcon: (filtered: boolean) => <FilterIcon filtered={filtered} />,
      render: (_: any, record: any) => (
        <div>
          <span className={interceptStatusMap[record?.interceptStatus]?.class}>
            {interceptStatusMap[record?.interceptStatus]?.label}
          </span>
        </div>
      ),
    },
    {
      title: t('告警结果'),
      dataIndex: 'alertLog',
      key: 'alertLog',
      width: '10%',
      render: (text: number) => (
        <div>
          <span className={logAlarmMap[text]?.class}>{logAlarmMap[text]?.label || '-'}</span>
        </div>
      ),
    },
    {
      title: t('执行时间'),
      dataIndex: 'sort_field_exec_time',
      key: 'sort_field_exec_time',
      width: '14%',
      sorter: (a: any, b: any) => a.execTime - b.execTime,
      render: (_: any, record: any) => (
        <Tooltip
          title={record.execTime ? record.execTime : '-'}
          getPopupContainer={() => document.body}
        >
          {record.execTime ? formatDateTime(record.execTime, 'mdhm') : '-'}
        </Tooltip>
      ),
    },
    {
      title: t('操作'),
      fixed: 'right',
      width: '10%',
      render: (_: any, record: any) => (
        <div
          className="operation"
          onClick={() => {
            getDrawerData(record.id)
            setActive(record.id)
          }}
        >
          <IconFont type="icon-chakan" />
          <span>{t('查看')}</span>
        </div>
      ),
    },
  ]

  // 如果有设备信息，添加设备结果分布列
  if (taskInfo?.devices?.length > 0) {
    const deviceColumn = {
      title: t('设备结果分布'),
      dataIndex: 'deviceStatus',
      key: 'deviceStatus',
      width: '12%',
      filteredValue: filterParams.deviceStatus,
      filterIcon: (filtered: boolean) => <FilterIcon filtered={filtered} />,
      filterDropdown: (props: FilterDropdownProps) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            mode="multiple"
            allowClear
            maxTagCount={'responsive'}
            style={{ width: '100%' }}
            placeholder={t('请选择设备结果')}
            value={props.selectedKeys as string[]}
            open={deviceStatusSelectOpen}
            getPopupContainer={() => document.body as HTMLElement}
            popupClassName="table-filter-dropdown"
            onDropdownVisibleChange={setDeviceStatusSelectOpen}
            onChange={value => {
              props.setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.clearFilters?.()
                    }}
                  >
                    重置
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.confirm()
                      setDeviceStatusSelectOpen(false)
                    }}
                  >
                    确定
                  </Button>
                </Space>
              </div>
            )}
            options={[
              { value: 'ResourceDeviceStatusDetectSuccess', label: t('检测成功') },
              { value: 'ResourceDeviceStatusInterceptSuccess', label: t('拦截成功') },
              { value: 'ResourceDeviceStatusProtectionInvalid', label: t('防护失效') },
              { value: 'ResourceDeviceStatusNotInvolved', label: t('不涉及') },
            ]}
          />
        </div>
      ),
      render: (_: any, record: any) => {
        const deviceStatusList: { name: string; count: number }[] = []
        const statusOrder = [
          { status: 'ResourceDeviceStatusDetectSuccess', name: t('检测成功') },
          { status: 'ResourceDeviceStatusInterceptSuccess', name: t('拦截成功') },
          { status: 'ResourceDeviceStatusProtectionInvalid', name: t('防护失效') },
          { status: 'ResourceDeviceStatusNotInvolved', name: t('不涉及') },
        ]

        statusOrder.forEach(orderItem => {
          const foundItem = record?.deviceStatusList?.find(
            (item: any) => item.status === orderItem.status,
          )
          deviceStatusList.push({
            name: orderItem.name,
            count: foundItem ? foundItem.count : 0,
          })
        })

        return (
          <div>
            {record?.deviceStatusList?.length === 1 && record.deviceStatusList[0]?.count === 1
              ? statusOrder.find(item => item.status === record?.deviceStatusList[0]?.status)?.name
              : record?.deviceStatusList?.length > 0
              ? deviceStatusList.map((item, index) => {
                  if (item.name === t('检测成功')) {
                    return (
                      <React.Fragment key={index}>
                        <Tooltip title={`${t('检测成功')}${item.count}`}>
                          <span style={{ color: '#72B851' }}>{item.count}</span>
                        </Tooltip>
                        <span style={{ margin: '0 4px' }}>|</span>
                      </React.Fragment>
                    )
                  } else if (item.name === t('拦截成功')) {
                    return (
                      <React.Fragment key={index}>
                        <Tooltip title={`${t('拦截成功')}${item.count}`}>
                          <span style={{ color: '#5677D9' }}>{item.count}</span>
                        </Tooltip>
                        <span style={{ margin: '0 4px' }}>|</span>
                      </React.Fragment>
                    )
                  } else if (item.name === t('防护失效')) {
                    return (
                      <React.Fragment key={index}>
                        <Tooltip title={`${t('防护失效')}${item.count}`}>
                          <span style={{ color: '#F44F4F' }}>{item.count}</span>
                        </Tooltip>
                        <span style={{ margin: '0 4px' }}>|</span>
                      </React.Fragment>
                    )
                  } else {
                    return (
                      <React.Fragment key={index}>
                        <Tooltip title={`${t('不涉及')}${item.count}`}>
                          <span style={{ color: '#333333' }}>{item.count}</span>
                        </Tooltip>
                      </React.Fragment>
                    )
                  }
                })
              : '-'}
          </div>
        )
      },
    }

    // 在告警结果列之前插入设备结果分布列
    const insertIndex = baseColumns.findIndex(col => col.key === 'alertLog')
    baseColumns.splice(insertIndex, 0, deviceColumn)
  }

  return baseColumns
}
